import { waContentOf } from "./dataJson";

// Menções em grupo. No texto, a menção chega como "@<id>": o id é o telefone ou o LID (o
// identificador interno do WhatsApp) de quem foi marcado — um número que o atendente não
// reconhece. Aqui ficam as regras para trocar esse id pelo nome do participante.

// "5511999998888:12@s.whatsapp.net" -> "5511999998888"
export const userOf = (jid) =>
  String(jid == null ? "" : jid)
    .split("@")[0]
    .split(":")[0]
    .replace(/\D/g, "");

const looksLikePhone = (digits) => digits.length >= 10 && digits.length <= 13;

export const formatPhone = (value) => {
  const digits = String(value == null ? "" : value).replace(/\D/g, "");
  if (!looksLikePhone(digits)) return "";
  const br = digits.length >= 12 && digits.startsWith("55") ? digits.slice(2) : digits.length <= 11 ? digits : "";
  if (br && (br.length === 10 || br.length === 11)) {
    const ddd = br.slice(0, 2);
    const rest = br.slice(2);
    const local = `${rest.slice(0, rest.length - 4)}-${rest.slice(-4)}`;
    return digits.length >= 12 ? `+55 (${ddd}) ${local}` : `(${ddd}) ${local}`;
  }
  return `+${digits}`;
};

// O servidor ainda devolve participantes incompletos: nome igual ao id, "número" que é o
// próprio LID. Aqui sobra só o que serve para mostrar.
export const normalizeParticipant = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = String(raw.id || "");
  const user = userOf(id);
  if (!user) return null;
  const lid = userOf(raw.lid) || (id.endsWith("@lid") ? user : "");
  const numberDigits = String(raw.number || "").replace(/\D/g, "");
  const number = numberDigits && numberDigits !== lid && looksLikePhone(numberDigits) ? numberDigits : "";
  const rawName = String(raw.name || "").trim();
  const name = rawName && !/^[\d\s+()@.:-]+$/.test(rawName) && rawName !== id ? rawName : "";
  return {
    id,
    user,
    lid,
    number,
    name,
    isAdmin: !!(raw.isAdmin || raw.isSuperAdmin),
    profilePicUrl: typeof raw.profilePicUrl === "string" && !/nopicture/i.test(raw.profilePicUrl) ? raw.profilePicUrl : "",
  };
};

// Mapa por id, LID e número (só dígitos) -> participante.
export const buildParticipantIndex = (participants) => {
  const index = new Map();
  (Array.isArray(participants) ? participants : []).forEach((raw) => {
    const p = raw && raw.user !== undefined ? raw : normalizeParticipant(raw);
    if (!p) return;
    [p.user, p.lid, p.number].filter(Boolean).forEach((key) => {
      if (!index.has(key)) index.set(key, p);
    });
  });
  return index;
};

const MENTION_RE = /(^|[^\w@])@(\d{8,20})(?!\d)/g;

// Pedaços do texto: [{ text }] e [{ mention: "<dígitos>" }], na ordem.
export const splitMentions = (text) => {
  const source = String(text == null ? "" : text);
  const parts = [];
  let last = 0;
  let match;
  MENTION_RE.lastIndex = 0;
  while ((match = MENTION_RE.exec(source)) !== null) {
    const start = match.index + match[1].length;
    if (start > last) parts.push({ text: source.slice(last, start) });
    parts.push({ mention: match[2] });
    last = MENTION_RE.lastIndex;
  }
  if (last < source.length) parts.push({ text: source.slice(last) });
  return parts;
};

export const mentionTokensOf = (text) => splitMentions(text).filter((p) => p.mention).map((p) => p.mention);

const mentionedCache = new WeakMap();

// Ids marcados segundo a própria mensagem: `mentionedJids` (a lista de mensagens do servidor
// já traz pronto) ou, na mensagem que chegou pelo socket, contextInfo.mentionedJid de qualquer
// tipo de conteúdo do WhatsApp. null = a mensagem não trouxe essa informação.
export const mentionedIdsOf = (message) => {
  if (!message || typeof message !== "object") return null;
  if (mentionedCache.has(message)) return mentionedCache.get(message);
  let ids = null;
  try {
    if (Array.isArray(message.mentionedJids)) {
      message.mentionedJids.forEach((jid) => {
        const user = userOf(jid);
        if (!user) return;
        if (!ids) ids = new Set();
        ids.add(user);
      });
    }
    const content = ids ? null : waContentOf(message);
    if (content) {
      Object.keys(content).forEach((key) => {
        const node = content[key];
        const list = node && typeof node === "object" && node.contextInfo && node.contextInfo.mentionedJid;
        if (!Array.isArray(list)) return;
        list.forEach((jid) => {
          const user = userOf(jid);
          if (!user) return;
          if (!ids) ids = new Set();
          ids.add(user);
        });
      });
    }
  } catch (e) {
    ids = null;
  }
  mentionedCache.set(message, ids);
  return ids;
};

// Como mostrar um "@<dígitos>". Devolve null quando não é menção (fica o texto como veio).
//  - participant: quem o id identifica, se conhecido (lista do grupo ou consulta ao servidor)
//  - mentioned: ids que a mensagem declarou como marcados (ou null)
export const describeMention = (digits, { participant, mentioned, fallbackLabel }) => {
  const declared = !!mentioned && mentioned.has(digits);
  // sem a declaração, só é menção o que casa com um participante — ou o que só pode ser um
  // LID (mais dígitos do que um telefone tem)
  if (!declared && !participant && digits.length < 14) return null;
  const phone = formatPhone((participant && participant.number) || (looksLikePhone(digits) ? digits : ""));
  if (participant && participant.name) {
    return { label: `@${participant.name}`, title: phone || undefined };
  }
  if (phone) return { label: `@${phone}`, title: undefined };
  // LID sem nome: nunca o número cru de 14+ dígitos no meio do texto
  return { label: `@${fallbackLabel || "participante"}`, title: digits };
};
